import type { ComponentType } from 'preact'
import { useMemo, useState } from 'preact/hooks'
import type { RunnerSlot } from '../runnerPreview/roster'
import { assetUrl } from '../runnerPreview/previewLayout'
import styles from './panels.module.css'
import { PLACEHOLDER_FRIENDS, PLACEHOLDER_LEAGUE, placeholderRows } from './placeholders'
import type { PlaceholderRow } from './placeholders'

type Tab = 'league' | 'friends'

/** The last tab picked on this page: the hub remounts after a join, the tab stays. */
let lastTab: Tab = 'league'

const STATUS_TEXT: Record<PlaceholderRow['status'], string> = {
  online: 'Invite',
  racing: 'Racing',
  offline: 'Offline',
}

function Avatar({ runner }: { runner: RunnerSlot | undefined }) {
  const head = runner?.rig?.faces.calm ?? runner?.image
  return (
    <span class={styles.avatar} style={{ '--runner': runner?.color ?? '#8a97ad' }}>
      {head ? <img src={assetUrl(head)} alt="" draggable={false} /> : null}
    </span>
  )
}

function Row({ row, runners }: { row: PlaceholderRow; runners: readonly RunnerSlot[] }) {
  const ready = runners.filter((r) => r.ready)
  const runner = ready.length > 0 ? ready[row.avatar % ready.length] : runners[0]
  return (
    <li class={`${styles.socialRow} ${row.isMe ? styles.me : ''}`}>
      <span class={styles.rank}>#{row.rank}</span>
      <Avatar runner={runner} />
      <span class={styles.rowName}>{row.isMe ? `${row.name} (you)` : row.name}</span>
      {/* Your own rating is unknown until the league exists (T3.14): a placeholder dash. */}
      <span class={styles.points}>{row.isMe ? '—' : row.points.toLocaleString('en-US')}</span>
      {row.isMe ? (
        <span />
      ) : (
        <span class={`${styles.status} ${styles[row.status]}`}>{STATUS_TEXT[row.status]}</span>
      )}
    </li>
  )
}

/**
 * League / Friends panel (PHASE3 T3.1): tabs, a search box and ranked rows. Until T3.13 / T3.14
 * the rows are `PLACEHOLDER_*` data under a dark overlay: "Sign in to unlock" for guests (with
 * Phase 2's sign-in buttons when they exist, D-053), "Coming soon" for signed-in players.
 */
export function SocialPanel({
  myName,
  guest,
  runners,
  SignIn,
}: {
  myName: string
  guest: boolean
  runners: readonly RunnerSlot[]
  SignIn: ComponentType | null
}) {
  const [tab, setTab] = useState<Tab>(() => lastTab)
  const pick = (t: Tab): void => {
    lastTab = t
    setTab(t)
  }
  const rows = useMemo(
    () => placeholderRows(tab === 'league' ? PLACEHOLDER_LEAGUE : PLACEHOLDER_FRIENDS, myName),
    [tab, myName],
  )
  return (
    <section class={`${styles.panel} ${styles.social}`} aria-label="League and friends">
      <div class={styles.smallTabs} role="tablist">
        {(['league', 'friends'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            class={`${styles.smallTab} ${tab === t ? styles.tabOn : ''}`}
            onClick={() => pick(t)}
          >
            {t === 'league' ? 'League' : 'Friends'}
          </button>
        ))}
      </div>
      <input
        class={styles.search}
        type="search"
        placeholder="Find a racer (coming soon)"
        aria-label="Find a racer (coming soon)"
        disabled
      />
      <div class={styles.listWrap}>
        <ol class={styles.socialList} aria-hidden="true">
          {rows.map((r) => (
            <Row key={r.rank} row={r} runners={runners} />
          ))}
        </ol>
        <div class={styles.lockOverlay}>
          <strong>{guest ? 'Sign in to unlock' : 'Coming soon'}</strong>
          <span>
            {tab === 'league'
              ? 'Climb the league by racing well.'
              : 'Add friends and invite them to races.'}
          </span>
          {guest && SignIn ? <SignIn /> : null}
        </div>
      </div>
    </section>
  )
}
